import React from 'react'

const MONOSPACE_STACK =
  'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

const CHIP_FILL = '#FFFFFF'
const CHIP_STROKE = '#59636B'
const TEXT_FILL = '#0F1417'

const FONT_SIZE_PER_HEIGHT = 0.87
const ADVANCE_PER_FONT_SIZE = 0.6
const PADDING_CHARACTERS = 0.55
const LINE_SPACING = 1.15

const CORNER_RADIUS = 2
const STROKE_WIDTH = 1.5
const TEXT_CENTRE_PER_HEIGHT = 0.5

const round = (value: number): number => Math.round(value * 1000) / 1000

const characterCount = (text: string): number =>
  Math.max(Array.from(text).length, 1)

/**
 * Width this label needs for `text`, as a multiple of its height, at the point
 * where height rather than width limits the type size. Every character
 * advances the same distance in a monospace face, so the label is one advance
 * per character plus the padding either side. Layout asks the label how wide it
 * wants to be rather than deriving it, so the two cannot drift apart.
 */
export function liveValueLabelWidthPerHeight(text: string): number {
  return (
    FONT_SIZE_PER_HEIGHT *
    ADVANCE_PER_FONT_SIZE *
    (characterCount(text) + PADDING_CHARACTERS)
  )
}

export interface LiveValueLabelProps {
  x: number
  y: number
  width: number
  height: number
  text: string
  stale?: boolean
}

/**
 * Live process reading drawn over a tag's bounding box, as an opaque chip with
 * monospace type on it. The chip hides the drawing's linework so nothing
 * competes with the digits, and the type size is taken from whichever of the
 * box's height and width binds first, so the value never overflows the box it
 * was given. An engineering unit wraps onto a second line below the value so
 * both stay legible inside the box.
 *
 * A reading older than an hour is indicated by a glow drawn around the chip
 * by the chip layer rather than by fading the chip itself, so the chip stays
 * fully legible. Nothing here captures pointer events, so the OCR box
 * underneath stays interactive.
 */
export function LiveValueLabel({
  x,
  y,
  width,
  height,
  text,
  stale = false,
}: LiveValueLabelProps): React.JSX.Element {
  const spaceIndex = text.indexOf(' ')
  const lines =
    spaceIndex === -1
      ? [text]
      : [text.slice(0, spaceIndex), text.slice(spaceIndex + 1)]
  const maxLineChars = Math.max(...lines.map(characterCount))

  const heightBudget =
    (height * FONT_SIZE_PER_HEIGHT) / (1 + (lines.length - 1) * LINE_SPACING)
  const widthBudget =
    width / (ADVANCE_PER_FONT_SIZE * (maxLineChars + PADDING_CHARACTERS))
  const fontSize = Math.max(Math.min(heightBudget, widthBudget), 0)

  const centreY = y + height * TEXT_CENTRE_PER_HEIGHT
  const lineOffset = (LINE_SPACING * fontSize) / 2

  return (
    <g
      pointerEvents='none'
      data-live-value={text}
      data-live-value-stale={stale ? '' : undefined}>
      <rect
        x={round(x)}
        y={round(y)}
        width={round(width)}
        height={round(height)}
        rx={CORNER_RADIUS}
        ry={CORNER_RADIUS}
        fill={CHIP_FILL}
        stroke={CHIP_STROKE}
        strokeWidth={STROKE_WIDTH}
      />
      {fontSize > 0 && (
        <text
          x={round(x + width / 2)}
          fill={TEXT_FILL}
          fontFamily={MONOSPACE_STACK}
          fontSize={round(fontSize)}
          fontWeight={700}
          textAnchor='middle'
          data-live-value-text>
          {lines.map((line, index) => (
            <tspan
              key={index}
              x={round(x + width / 2)}
              y={round(
                lines.length === 1
                  ? centreY
                  : centreY + (index === 0 ? -lineOffset : lineOffset)
              )}
              dominantBaseline='central'>
              {line}
            </tspan>
          ))}
        </text>
      )}
    </g>
  )
}

export default LiveValueLabel
